import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Radio as RadioIcon, Loader2, Check, CheckCircle2, PlayCircle, XCircle, Clock, Download, Save, SlidersHorizontal, ChevronDown, PhoneCall, Phone } from "lucide-react";
import { sr } from "../../lib/sr";

const STATUS_STYLE = {
  DRAFT: { bg: "#F2F4F7", color: "#475467", label: "Draft" },
  CONFIRMED: { bg: "#EFF4FF", color: "#2E6BE6", label: "Rates confirmed" },
  LIVE: { bg: "#E7F7EF", color: "#12805C", label: "On air" },
  COMPLETED: { bg: "#F4F0FF", color: "#6941C6", label: "Completed" },
  CANCELLED: { bg: "#FEF3F2", color: "#B42318", label: "Cancelled" },
};

function money(v, symbol) {
  return `${symbol || "$"}${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
function symFor(cur) { return cur === "AED" ? "AED " : "$"; }
function timeAgo(iso) {
  if (!iso) return "";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
}

function CallTrackingCard({ flight, onChanged }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ business_number: "", area_code: "", demo: true, confirm_purchase: false });
  const [fmsg, setFmsg] = useState("");
  const ct = flight.call_tracking;

  const load = () => sr.adminFlightCallTracking(flight.id)
    .then((d) => { setData(d); setForm((f) => ({ ...f, business_number: f.business_number || d.suggested_business_number || "" })); if (d.mapping) setFmsg(d.mapping.followup_message || ""); })
    .catch(() => setData({ mapping: null, calls: [], enabled: false, can_provision: false }));

  useEffect(() => { if (open && !data) load(); /* eslint-disable-next-line */ }, [open]);

  const provision = async () => {
    if (!form.business_number.trim()) { toast.error("Enter the business phone to ring through to"); return; }
    setBusy("provision");
    try {
      await sr.provisionFlightCallNumber(flight.id, {
        business_number: form.business_number.trim(), area_code: form.area_code.trim() || null,
        demo: form.demo, confirm_purchase: form.confirm_purchase,
      });
      toast.success(form.demo ? "Demo tracking number assigned" : "Live tracking number provisioned");
      setData(null); load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not assign a number"); }
    finally { setBusy(""); }
  };
  const release = async () => {
    setBusy("release");
    try {
      await sr.releaseFlightCallNumber(flight.id, { confirm: true });
      toast.success("Number released");
      setData(null); load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not release"); }
    finally { setBusy(""); }
  };
  const simulate = async (outcome) => {
    setBusy("sim" + outcome);
    try { await sr.simulateFlightCall(flight.id, outcome); toast.success(outcome === "missed" ? "Logged a missed demo call" : "Logged a demo call"); setData(null); load(); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not simulate"); }
    finally { setBusy(""); }
  };
  const saveAlerts = async (body, key) => {
    setBusy(key || "alerts");
    try { await sr.setFlightCallAlerts(flight.id, body); setData(null); load(); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy(""); }
  };

  return (
    <div style={{ marginTop: 10, borderTop: "1px dashed var(--border)", paddingTop: 10 }}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`calltracking-toggle-${flight.id}`}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, color: "var(--navy)", fontWeight: 700, fontSize: 13.5 }}>
          <PhoneCall size={14} style={{ color: "var(--blue)" }} /> Call tracking
          {ct?.number
            ? <span className="sr-badge" style={{ background: "#EEF4FF", color: "var(--blue)" }} data-testid={`calltracking-badge-${flight.id}`}>{ct.number} · {ct.calls} call{ct.calls === 1 ? "" : "s"}</span>
            : <span className="sr-muted" style={{ fontWeight: 400, fontSize: 12 }}>not assigned</span>}
        </span>
        <ChevronDown size={15} style={{ transition: "transform .18s", transform: open ? "rotate(0deg)" : "rotate(-90deg)", color: "var(--muted)" }} />
      </button>

      {open && (
        <div style={{ marginTop: 10 }} data-testid={`calltracking-panel-${flight.id}`}>
          {data === null ? (
            <div style={{ padding: 8 }}><Loader2 className="sr-spin" size={15} /></div>
          ) : data.mapping ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "var(--surface)", borderRadius: 8, padding: "10px 12px" }}>
                <Phone size={15} style={{ color: "var(--blue)" }} />
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 14 }} data-testid={`calltracking-number-${flight.id}`}>{data.mapping.number}
                    {data.mapping.simulated && <span className="sr-badge" style={{ background: "#FFF4E5", color: "#B7791F", marginLeft: 6 }}>Demo</span>}
                  </div>
                  <div className="sr-muted" style={{ fontSize: 12 }}>Rings through to {data.mapping.business_number} · {data.calls.length} call{data.calls.length === 1 ? "" : "s"}{data.missed ? ` · ${data.missed} missed` : ""}</div>
                </div>
                {data.mapping.simulated && (
                  <>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => simulate("answered")} disabled={!!busy} data-testid={`calltracking-simulate-${flight.id}`}>
                      {busy === "simanswered" ? <Loader2 size={13} className="sr-spin" /> : "Log call"}
                    </button>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => simulate("missed")} disabled={!!busy} data-testid={`calltracking-simulate-missed-${flight.id}`} style={{ color: "#B42318" }}>
                      {busy === "simmissed" ? <Loader2 size={13} className="sr-spin" /> : "Log missed"}
                    </button>
                  </>
                )}
                {data.can_provision && (
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={release} disabled={!!busy} style={{ color: "#B42318" }} data-testid={`calltracking-release-${flight.id}`}>
                    {busy === "release" ? <Loader2 size={13} className="sr-spin" /> : "Release"}
                  </button>
                )}
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12.5, color: "var(--navy)" }}>
                <input type="checkbox" checked={data.mapping.missed_alerts !== false} disabled={!!busy} onChange={(e) => saveAlerts({ missed_alerts: e.target.checked })} data-testid={`calltracking-alerts-${flight.id}`} />
                Text &amp; email the business the moment a tracked call goes unanswered
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 12.5, color: "var(--navy)" }}>
                <input type="checkbox" checked={!!data.mapping.auto_followup} disabled={!!busy} onChange={(e) => saveAlerts({ auto_followup: e.target.checked }, "followup")} data-testid={`calltracking-followup-${flight.id}`} />
                Auto-text the missed caller a friendly "sorry we missed you" from the business
              </label>
              {data.mapping.auto_followup && (
                <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "flex-start" }}>
                  <textarea value={fmsg} onChange={(e) => setFmsg(e.target.value)} rows={2} maxLength={320}
                    placeholder={'Sorry we missed your call! Reply here or call us back and we\'ll be glad to help.'}
                    data-testid={`calltracking-followup-msg-${flight.id}`}
                    style={{ flex: 1, padding: "7px 9px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12.5, resize: "vertical" }} />
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy === "followup-msg"} onClick={() => saveAlerts({ followup_message: fmsg }, "followup-msg")} data-testid={`calltracking-followup-save-${flight.id}`}>
                    {busy === "followup-msg" ? <Loader2 size={13} className="sr-spin" /> : "Save"}
                  </button>
                </div>
              )}
              {data.calls.length > 0 && (
                <div style={{ marginTop: 8, display: "grid", gap: 4 }} data-testid={`calltracking-calls-${flight.id}`}>
                  {data.calls.slice(0, 6).map((c) => (
                    <div key={c.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--navy)", padding: "4px 8px", background: "#fff", border: "1px solid var(--border)", borderRadius: 6 }}>
                      <span>{c.caller || "Unknown"}</span>
                      <span className="sr-muted">{c.status}{c.duration_sec ? ` · ${c.duration_sec}s` : ""} · {timeAgo(c.received_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : !data.can_provision ? (
            <p className="sr-muted" style={{ fontSize: 12.5 }}>Only an admin can assign a call-tracking number to this flight.</p>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              <p className="sr-muted" style={{ fontSize: 12.5 }}>Give this flight its own number so every inbound call is attributed to it and forwarded to the business.</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <label className="sr-muted" style={{ fontSize: 11, display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 180 }}>
                  Ring through to (business phone)
                  <input value={form.business_number} onChange={(e) => setForm({ ...form, business_number: e.target.value })} placeholder="+1 469 555 0123" data-testid={`calltracking-business-${flight.id}`}
                    style={{ padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6 }} />
                </label>
                {!form.demo && (
                  <label className="sr-muted" style={{ fontSize: 11, display: "flex", flexDirection: "column", gap: 3, width: 110 }}>
                    Area code (opt)
                    <input value={form.area_code} onChange={(e) => setForm({ ...form, area_code: e.target.value.replace(/[^0-9]/g, "") })} placeholder="469" data-testid={`calltracking-area-${flight.id}`}
                      style={{ padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6 }} />
                  </label>
                )}
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--navy)" }}>
                <input type="checkbox" checked={form.demo} onChange={(e) => setForm({ ...form, demo: e.target.checked, confirm_purchase: false })} data-testid={`calltracking-demo-${flight.id}`} />
                Use a demo number (no purchase) — try the flow without spending
              </label>
              {!form.demo && (
                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, color: "var(--navy)" }}>
                  <input type="checkbox" checked={form.confirm_purchase} onChange={(e) => setForm({ ...form, confirm_purchase: e.target.checked })} data-testid={`calltracking-confirm-${flight.id}`} style={{ marginTop: 2 }} />
                  <span>I understand this buys a live number on the production Twilio account (monthly cost).{!data.enabled && <span style={{ color: "#B42318" }}> Live provisioning is currently off — turn on ENABLE_TWILIO_PROVISIONING after deploy.</span>}</span>
                </label>
              )}
              <div>
                <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={provision} disabled={!!busy || (!form.demo && (!form.confirm_purchase || !data.enabled))} data-testid={`calltracking-assign-${flight.id}`}>
                  {busy === "provision" ? <Loader2 size={13} className="sr-spin" /> : <><PhoneCall size={13} /> {form.demo ? "Assign demo number" : "Provision live number"}</>}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FlightCard({ flight, onChanged }) {
  const cur = flight.currency || "USD";
  const symbol = symFor(cur);
  const [rows, setRows] = useState(flight.stations || []);
  const [busy, setBusy] = useState("");
  const [dirty, setDirty] = useState(false);
  const st = STATUS_STYLE[flight.status] || STATUS_STYLE.DRAFT;

  const total = useMemo(() => rows.reduce((a, r) => a + Number(r.unit_price || 0) * Number(r.spots || 0), 0), [rows]);
  const totalSpots = useMemo(() => rows.reduce((a, r) => a + Number(r.spots || 0), 0), [rows]);

  const editRow = (key, field, val) => {
    setDirty(true);
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, [field]: Math.max(0, Number(val) || 0) } : r)));
  };

  const saveRates = async () => {
    setBusy("rates");
    try {
      const stations = rows.map((r) => ({ key: r.key, unit_price: Number(r.unit_price), spots: Number(r.spots) }));
      await sr.adminUpdateRadioFlight(flight.id, { stations, status: flight.status === "DRAFT" ? "CONFIRMED" : undefined });
      toast.success("Rates confirmed");
      setDirty(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save rates"); }
    finally { setBusy(""); }
  };
  const setStatus = async (status) => {
    setBusy(status);
    try {
      await sr.adminUpdateRadioFlight(flight.id, { status });
      toast.success(`Marked ${STATUS_STYLE[status].label.toLowerCase()}`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy(""); }
  };

  return (
    <div className="sr-card" style={{ marginBottom: 14 }} data-testid={`flight-${flight.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <RadioIcon size={16} style={{ color: "var(--gold)" }} />
            <strong style={{ color: "var(--navy)", fontSize: 15.5 }}>{flight.tenant_name}</strong>
            <span className="sr-badge" style={{ background: st.bg, color: st.color }} data-testid={`flight-status-${flight.id}`}>{st.label}</span>
            {flight.rates_confirmed && <span className="sr-badge sr-badge-green"><Check size={11} /> rates set</span>}
          </div>
          <div className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>{flight.network} · {flight.market} · <strong>{cur}</strong> · created {timeAgo(flight.created_at)}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "var(--navy)" }} data-testid={`flight-total-${flight.id}`}>{money(total, symbol)}/mo</div>
          <div className="sr-muted" style={{ fontSize: 12 }}>{totalSpots} spots/mo</div>
        </div>
      </div>

      <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
        {rows.map((r) => (
          <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "8px 10px", background: "var(--surface)", borderRadius: 8 }} data-testid={`flight-station-${flight.id}-${r.key}`}>
            {r.logo && <img src={r.logo} alt={r.name} width={30} height={30} style={{ borderRadius: 6, objectFit: "contain", background: "#fff" }} />}
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontWeight: 600, color: "var(--navy)", fontSize: 13.5 }}>{r.name} <span className="sr-muted" style={{ fontWeight: 400 }}>· {r.delivery}</span></div>
              {Array.isArray(r.dayparts) && <div className="sr-muted" style={{ fontSize: 11, marginTop: 2 }}>{r.dayparts.join(" · ")}</div>}
            </div>
            <label className="sr-muted" style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}>
              spots
              <input type="number" min={0} value={r.spots} onChange={(e) => editRow(r.key, "spots", e.target.value)} data-testid={`flight-spots-${flight.id}-${r.key}`}
                style={{ width: 62, padding: "4px 6px", border: "1px solid var(--border)", borderRadius: 6 }} />
            </label>
            <label className="sr-muted" style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}>
              {symbol}/spot
              <input type="number" min={0} step="0.5" value={r.unit_price} onChange={(e) => editRow(r.key, "unit_price", e.target.value)} data-testid={`flight-rate-${flight.id}-${r.key}`}
                style={{ width: 74, padding: "4px 6px", border: "1px solid var(--border)", borderRadius: 6 }} />
            </label>
            <div style={{ width: 90, textAlign: "right", fontWeight: 600, color: "var(--navy)", fontSize: 13 }}>{money(Number(r.unit_price || 0) * Number(r.spots || 0), symbol)}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={saveRates} disabled={!!busy} data-testid={`flight-confirm-${flight.id}`}>
          {busy === "rates" ? <Loader2 size={13} className="sr-spin" /> : <><CheckCircle2 size={13} /> {dirty ? "Save & confirm rates" : "Confirm rates"}</>}
        </button>
        {flight.status !== "LIVE" && flight.status !== "CANCELLED" && (
          <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={() => setStatus("LIVE")} disabled={!!busy} data-testid={`flight-live-${flight.id}`}>
            {busy === "LIVE" ? <Loader2 size={13} className="sr-spin" /> : <><PlayCircle size={13} /> Mark live</>}
          </button>
        )}
        {flight.status === "LIVE" && (
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setStatus("COMPLETED")} disabled={!!busy} data-testid={`flight-complete-${flight.id}`}>
            {busy === "COMPLETED" ? <Loader2 size={13} className="sr-spin" /> : <><Check size={13} /> Complete</>}
          </button>
        )}
        {flight.status !== "CANCELLED" && flight.status !== "COMPLETED" && (
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setStatus("CANCELLED")} disabled={!!busy} data-testid={`flight-cancel-${flight.id}`} style={{ color: "#B42318" }}>
            <XCircle size={13} /> Cancel
          </button>
        )}
      </div>

      <CallTrackingCard flight={flight} onChanged={onChanged} />
    </div>
  );
}

function DubaiRatesCard() {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const hydrate = (d) => {
    setData(d);
    const e = {};
    (d.stations || []).forEach((s) => {
      e[s.key] = {};
      (d.dayparts || []).forEach((dp) => {
        const v = (s.overrides || {})[dp.key];
        e[s.key][dp.key] = v === null || v === undefined ? "" : String(v);
      });
    });
    setEdits(e);
  };

  useEffect(() => { sr.adminDubaiRates().then(hydrate).catch(() => setData({ stations: [], dayparts: [] })); }, []);

  const canEdit = !!data?.can_edit;
  const setVal = (key, dp, val) => setEdits((e) => ({ ...e, [key]: { ...e[key], [dp]: val.replace(/[^0-9.]/g, "") } }));

  const save = async () => {
    setSaving(true);
    try {
      const rates = {};
      Object.entries(edits).forEach(([key, dps]) => {
        const row = {};
        Object.entries(dps).forEach(([dp, v]) => { if (v !== "" && Number(v) > 0) row[dp] = Number(v); });
        if (Object.keys(row).length) rates[key] = row;
      });
      const r = await sr.adminSetDubaiRates(rates);
      toast.success("Dubai daypart rates saved");
      const d = await sr.adminDubaiRates();
      hydrate(d);
      return r;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save rates"); }
    finally { setSaving(false); }
  };

  if (data && (data.stations || []).length === 0) return null;

  return (
    <div className="sr-card" style={{ marginBottom: 14, background: "#F8FAFF", border: "1px solid #C7D7FE" }} data-testid="dubai-rates-card">
      <button onClick={() => setOpen((o) => !o)} data-testid="dubai-rates-toggle"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--navy)", fontWeight: 700, fontSize: 15 }}>
          <SlidersHorizontal size={16} style={{ color: "var(--blue)" }} /> Dubai Daypart Rates <span className="sr-badge" style={{ background: "#EAF2FF", color: "var(--blue)" }}>AED</span>
        </span>
        <ChevronDown size={16} style={{ transition: "transform .18s", transform: open ? "rotate(0deg)" : "rotate(-90deg)", color: "var(--muted)" }} />
      </button>

      {open && (
        <div style={{ marginTop: 12 }}>
          {data === null ? (
            <div style={{ padding: 10 }}><Loader2 className="sr-spin" size={16} /></div>
          ) : (
            <>
              <p className="sr-muted" style={{ fontSize: 12.5, marginBottom: 10 }}>{data.note}</p>
              <div style={{ display: "grid", gap: 10 }}>
                {(data.stations || []).map((s) => (
                  <div key={s.key} data-testid={`dubai-rate-station-${s.key}`} style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                      {s.logo && <img src={s.logo} alt={s.name} width={28} height={28} style={{ borderRadius: 6, objectFit: "contain", background: "#fff" }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 13.5 }}>{s.name} <span className="sr-muted" style={{ fontWeight: 400 }}>· {s.delivery}</span></div>
                        <div className="sr-muted" style={{ fontSize: 11.5 }}>Base rate AED {s.base_rate}/spot{s.varies ? " · daypart rates active" : " · flat rate"}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {(data.dayparts || []).map((dp) => (
                        <label key={dp.key} className="sr-muted" style={{ fontSize: 11, display: "flex", flexDirection: "column", gap: 3 }}>
                          <span>{dp.drive_time ? "🚗 " : ""}{dp.label} <span style={{ opacity: 0.7 }}>({dp.time})</span></span>
                          <input type="number" min={0} step="1" inputMode="decimal"
                            value={edits[s.key]?.[dp.key] ?? ""} placeholder={`AED ${s.base_rate}`} disabled={!canEdit}
                            onChange={(e) => setVal(s.key, dp.key, e.target.value)}
                            data-testid={`dubai-rate-${s.key}-${dp.key}`}
                            style={{ width: 96, padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 6, background: canEdit ? "#fff" : "#F5F6F8" }} />
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
                {canEdit ? (
                  <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={save} disabled={saving} data-testid="dubai-rates-save">
                    {saving ? <Loader2 size={13} className="sr-spin" /> : <><Save size={13} /> Save Dubai rates</>}
                  </button>
                ) : (
                  <span className="sr-muted" style={{ fontSize: 12.5 }}>Only a Super Admin can change these rates.</span>
                )}
                <span className="sr-muted" style={{ fontSize: 12 }}>Blank = station's flat base rate.</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminRadioFlights() {
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [filter, setFilter] = useState("ALL");
  const [exporting, setExporting] = useState("");
  const [stationSel, setStationSel] = useState("");

  const load = () => sr.adminRadioFlights()
    .then((d) => { setData(d); setDenied(false); })
    .catch((e) => { if (e?.response?.status === 403) setDenied(true); setData({ flights: [], statuses: [], counts: {} }); });
  useEffect(() => { load(); }, []);

  const stationNames = useMemo(() => {
    const set = new Set();
    (data?.flights || []).forEach((f) => {
      (f.stations || []).forEach((s) => s?.name && set.add(s.name));
      if ((!f.stations || f.stations.length === 0) && f.station_name) set.add(f.station_name);
    });
    return Array.from(set).sort();
  }, [data]);

  const doExport = async (format, station) => {
    setExporting(station ? "station" : format);
    try {
      const blob = await sr.adminRadioFlightsExport(format, filter, station || undefined);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = (station || filter).toLowerCase().replace(/[^a-z0-9]+/g, "-");
      a.download = `radio-runsheet-${slug}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(station ? `${station} run-sheet exported` : `Run-sheet exported (${format.toUpperCase()})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not export run-sheet"); }
    finally { setExporting(""); }
  };

  const flights = (data?.flights || []).filter((f) => filter === "ALL" || f.status === filter);

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 4px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
        <div>
          <h1 style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><RadioIcon size={22} /> Radio Flight Console</h1>
          <p className="sr-muted" style={{ fontSize: 14, marginTop: 2 }}>Confirm station rates and mark bookings live across every client, per station.</p>
        </div>
        <div style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
          {!denied && data && (
            <>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => doExport("csv")} disabled={!!exporting} data-testid="flights-export-csv">
                {exporting === "csv" ? <Loader2 size={13} className="sr-spin" /> : <><Download size={13} /> CSV</>}
              </button>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => doExport("pdf")} disabled={!!exporting} data-testid="flights-export-pdf">
                {exporting === "pdf" ? <Loader2 size={13} className="sr-spin" /> : <><Download size={13} /> PDF run-sheet</>}
              </button>
            </>
          )}
          <button className="sr-btn sr-btn-sm" onClick={load} data-testid="flights-refresh">Refresh</button>
        </div>
      </div>

      {denied ? (
        <div className="sr-card" style={{ textAlign: "center", padding: 44 }} data-testid="flights-denied">
          <RadioIcon size={28} color="var(--muted)" />
          <h3 style={{ marginTop: 12 }}>Staff only</h3>
          <p className="sr-muted" style={{ fontSize: 14 }}>Only SmartReach staff can manage radio flights.</p>
        </div>
      ) : data === null ? (
        <div className="sr-card" style={{ textAlign: "center", padding: 40 }}><Loader2 className="sr-spin" /></div>
      ) : (
        <>
          <DubaiRatesCard />
          {stationNames.length > 0 && (
            <div className="sr-card" style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }} data-testid="station-runsheet-card">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 7, color: "var(--navy)", fontWeight: 700, fontSize: 13.5 }}>
                <Download size={14} style={{ color: "var(--gold)" }} /> Per-station run-sheet
              </span>
              <select value={stationSel} onChange={(e) => setStationSel(e.target.value)} data-testid="station-runsheet-select"
                style={{ padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 8, minWidth: 200 }}>
                <option value="">Choose a station…</option>
                {stationNames.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={!stationSel || !!exporting} onClick={() => doExport("pdf", stationSel)} data-testid="station-runsheet-pdf">
                {exporting === "station" ? <Loader2 size={13} className="sr-spin" /> : <><Download size={13} /> Station PDF</>}
              </button>
              <span className="sr-muted" style={{ fontSize: 12 }}>What airs and when, for one station's traffic desk.</span>
            </div>
          )}
          <div style={{ display: "inline-flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }} data-testid="flights-filters">
            {["ALL", ...(data.statuses || [])].map((s) => (
              <button key={s} className={`sr-btn sr-btn-sm ${filter === s ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setFilter(s)} data-testid={`flights-filter-${s}`}>
                {s === "ALL" ? "All" : (STATUS_STYLE[s]?.label || s)}{s !== "ALL" && data.counts?.[s] ? ` (${data.counts[s]})` : ""}
              </button>
            ))}
          </div>
          {flights.length === 0 ? (
            <div className="sr-card" style={{ textAlign: "center", padding: 44 }} data-testid="flights-empty">
              <Clock size={26} color="var(--muted)" />
              <h3 style={{ marginTop: 10 }}>No radio bookings yet</h3>
              <p className="sr-muted" style={{ fontSize: 14 }}>When clients approve a radio plan, it lands here for you to confirm and air.</p>
            </div>
          ) : (
            <div data-testid="flights-list">
              {flights.map((f) => <FlightCard key={f.id} flight={f} onChanged={load} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
